import React, { useEffect, useState } from 'react';
import moment from 'moment';
import api from '../utils/api';

const HoursSummary = ({ employees, schedules }) => {
    const [hoursData, setHoursData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedView, setSelectedView] = useState('weekly');
    const [currentDate, setCurrentDate] = useState(moment());
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [isCollapsed, setIsCollapsed] = useState(false);

    const calculateHoursData = () => {
        try {
            setLoading(true);
            const employeeHours = {};
            let startDate, endDate;

            // Set date range based on selected view
            if (selectedView === 'weekly') {
                // Configure moment to start weeks on Sunday
                moment.updateLocale('en', {
                    week: {
                        dow: 0  // Sunday is 0, Monday is 1, etc.
                    }
                });
                
                startDate = currentDate.clone().startOf('week');
                endDate = currentDate.clone().endOf('week');
                
                console.log('Week range calculation:', {
                    currentDate: currentDate.format('YYYY-MM-DD'),
                    weekStart: startDate.format('YYYY-MM-DD'),
                    weekEnd: endDate.format('YYYY-MM-DD'),
                    dayOfWeek: currentDate.day()
                });
            } else if (selectedView === 'monthly') {
                // For monthly view, we'll use the first day of the month to the last day
                startDate = currentDate.clone().startOf('month');
                endDate = currentDate.clone().endOf('month');
                
                // Log the full month range we're calculating
                console.log('Monthly view date range:', {
                    startOfMonth: startDate.format('YYYY-MM-DD'),
                    endOfMonth: endDate.format('YYYY-MM-DD'),
                    totalDays: endDate.diff(startDate, 'days') + 1
                });
            } else {
                startDate = currentDate.clone().startOf('year');
                endDate = currentDate.clone().endOf('year');
            }

            console.log('Calculating hours for period:', {
                startDate: startDate.format('YYYY-MM-DD HH:mm:ss'),
                endDate: endDate.format('YYYY-MM-DD HH:mm:ss'),
                selectedView,
                currentDate: currentDate.format('YYYY-MM-DD')
            });
            
            // Initialize hours data for all employees
            employees.forEach(employee => {
                employeeHours[employee.id] = {
                    total: 0,
                    daily: {}
                };
            });

            // Process each schedule
            console.log('All schedules before processing:', schedules.map(s => ({
                id: s.id,
                resource: s.resource,
                start: moment(s.start).format('YYYY-MM-DD HH:mm:ss'),
                end: moment(s.end).format('YYYY-MM-DD HH:mm:ss'),
                hoursWorked: moment.duration(moment(s.end).diff(moment(s.start))).asHours()
            })));

            // Track monthly totals for debugging
            const monthlyTotals = {};
            
            schedules.forEach(schedule => {
                const employeeId = schedule.resource;
                if (!employeeId) {
                    console.log('Skipping schedule - no employeeId:', schedule);
                    return;
                }

                const start = moment(schedule.start);
                let end = moment(schedule.end);
                
                // If end time is 00:00:00, adjust it to be the next day
                if (end.format('HH:mm:ss') === '00:00:00') {
                    end = end.add(1, 'day');
                    console.log('Adjusted midnight end time:', {
                        scheduleId: schedule.id,
                        originalEnd: schedule.end,
                        adjustedEnd: end.format('YYYY-MM-DD HH:mm:ss')
                    });
                }
                
                // For monthly view, ensure we're including all events in the month
                const isInRange = selectedView === 'monthly' 
                    ? start.format('YYYY-MM') === currentDate.format('YYYY-MM')
                    : (selectedView === 'weekly' 
                        ? start.isBetween(startDate, endDate, 'day', '[]')
                        : start.isSame(currentDate, 'day'));

                console.log('Schedule check details:', {
                    scheduleId: schedule.id,
                    start: start.format('YYYY-MM-DD HH:mm:ss'),
                    end: end.format('YYYY-MM-DD HH:mm:ss'),
                    weekStart: startDate.format('YYYY-MM-DD'),
                    weekEnd: endDate.format('YYYY-MM-DD'),
                    currentDate: currentDate.format('YYYY-MM-DD'),
                    isInRange,
                    selectedView,
                    matchesWeek: start.isBetween(startDate, endDate, 'day', '[]'),
                    matchesMonth: start.format('YYYY-MM') === currentDate.format('YYYY-MM')
                });

                // Calculate hours with adjusted end time
                const hoursWorked = moment.duration(end.diff(start)).asHours();

                console.log('Checking schedule:', {
                    id: schedule.id,
                    employeeId,
                    start: start.format('YYYY-MM-DD HH:mm:ss'),
                    end: end.format('YYYY-MM-DD HH:mm:ss'),
                    originalEnd: schedule.end,
                    isInRange,
                    selectedView,
                    currentYearMonth: currentDate.format('YYYY-MM'),
                    scheduleYearMonth: start.format('YYYY-MM'),
                    monthMatch: start.format('YYYY-MM') === currentDate.format('YYYY-MM'),
                    hoursWorked,
                    weekStart: startDate.format('YYYY-MM-DD'),
                    weekEnd: endDate.format('YYYY-MM-DD')
                });

                if (!isInRange) {
                    console.log('Schedule excluded - out of range:', {
                        id: schedule.id,
                        start: start.format('YYYY-MM-DD HH:mm:ss'),
                        end: end.format('YYYY-MM-DD HH:mm:ss'),
                        currentYearMonth: currentDate.format('YYYY-MM'),
                        scheduleYearMonth: start.format('YYYY-MM')
                    });
                    return;
                }

                const shiftDate = start.format('YYYY-MM-DD');

                // Track monthly totals with corrected hours
                if (start.format('YYYY-MM') === currentDate.format('YYYY-MM')) {
                    if (!monthlyTotals[employeeId]) {
                        monthlyTotals[employeeId] = 0;
                    }
                    monthlyTotals[employeeId] += hoursWorked;
                }

                console.log('Including schedule in total:', {
                    scheduleId: schedule.id,
                    employeeId,
                    shiftDate,
                    start: start.format('YYYY-MM-DD HH:mm:ss'),
                    end: end.format('YYYY-MM-DD HH:mm:ss'),
                    hoursWorked,
                    view: selectedView,
                    runningMonthlyTotal: monthlyTotals[employeeId]
                });

                // Add to employee's daily and total hours
                if (!employeeHours[employeeId].daily[shiftDate]) {
                    employeeHours[employeeId].daily[shiftDate] = {
                        hours: 0,
                        scheduleId: schedule.id
                    };
                }
                
                console.log('Adding hours to daily total:', {
                    employeeId,
                    shiftDate,
                    existingHours: employeeHours[employeeId].daily[shiftDate].hours,
                    addingHours: hoursWorked,
                    scheduleId: schedule.id,
                    newTotal: employeeHours[employeeId].daily[shiftDate].hours + hoursWorked,
                    fullSchedule: schedule // Log the full schedule object
                });
                
                // Update hours but keep the original schedule ID
                employeeHours[employeeId].daily[shiftDate].hours += hoursWorked;
                // Ensure we keep the schedule ID even when adding more hours
                employeeHours[employeeId].daily[shiftDate].scheduleId = schedule.id;
                employeeHours[employeeId].total += hoursWorked;

                console.log('Updated daily entry:', {
                    employeeId,
                    shiftDate,
                    entry: employeeHours[employeeId].daily[shiftDate]
                });
            });

            console.log('Monthly totals for comparison:', monthlyTotals);
            console.log('Employee hours before formatting:', employeeHours);

            // Convert daily hours to array format and round numbers
            Object.keys(employeeHours).forEach(employeeId => {
                const dailyBreakdown = Object.entries(employeeHours[employeeId].daily).map(([date, data]) => {
                    console.log('Converting daily hours:', {
                        employeeId,
                        date,
                        hours: data.hours,
                        scheduleId: data.scheduleId,
                        roundedHours: Math.round(data.hours * 100) / 100
                    });
                    return {
                        work_date: date,
                        hours: Math.round(data.hours * 100) / 100,
                        scheduleId: data.scheduleId
                    };
                });

                dailyBreakdown.sort((a, b) => moment(a.work_date).diff(moment(b.work_date)));
                
                console.log('Final daily breakdown for employee:', {
                    employeeId,
                    dailyBreakdown
                });

                employeeHours[employeeId] = {
                    total: Math.round(employeeHours[employeeId].total * 100) / 100,
                    daily: dailyBreakdown
                };
            });

            console.log('Final hours data:', employeeHours);
            setHoursData(employeeHours);
            setLoading(false);
            setError(null);
        } catch (err) {
            console.error('Error calculating hours:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    const navigatePrevious = () => {
        const newDate = currentDate.clone();
        if (selectedView === 'weekly') {
            // Ensure we move to the previous week's Sunday
            newDate.subtract(1, 'week').startOf('week');
            console.log('Navigating to previous week:', {
                from: currentDate.format('YYYY-MM-DD'),
                to: newDate.format('YYYY-MM-DD')
            });
        } else if (selectedView === 'monthly') {
            newDate.subtract(1, 'month');
        } else {
            newDate.subtract(1, 'year');
        }
        setCurrentDate(newDate);
    };

    const navigateNext = () => {
        const newDate = currentDate.clone();
        if (selectedView === 'weekly') {
            // Ensure we move to the next week's Sunday
            newDate.add(1, 'week').startOf('week');
            console.log('Navigating to next week:', {
                from: currentDate.format('YYYY-MM-DD'),
                to: newDate.format('YYYY-MM-DD')
            });
        } else if (selectedView === 'monthly') {
            newDate.add(1, 'month');
        } else {
            newDate.add(1, 'year');
        }
        setCurrentDate(newDate);
    };

    const getCurrentPeriodLabel = () => {
        if (selectedView === 'weekly') {
            const start = currentDate.clone().startOf('week');
            const end = currentDate.clone().endOf('week');
            return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
        } else if (selectedView === 'monthly') {
            return currentDate.format('MMMM YYYY');
        } else {
            return currentDate.format('YYYY');
        }
    };

    // Calculate hours when schedules, employees, view, or current date changes
    useEffect(() => {
        if (employees.length > 0 && schedules) {
            calculateHoursData();
        }
    }, [employees, schedules, selectedView, currentDate]);

    // Expose refresh function globally
    useEffect(() => {
        window.refreshHoursSummary = () => {
            console.log('Manual refresh triggered');
            setLastUpdate(Date.now());
        };
        return () => {
            delete window.refreshHoursSummary;
        };
    }, []);

    // Refresh data every minute and when employees or view changes
    useEffect(() => {
        if (employees.length > 0) {
            console.log('Fetching hours data due to dependency change:', {
                employeesCount: employees.length,
                selectedView,
                lastUpdate
            });
            calculateHoursData();
        }
        
        const interval = setInterval(() => {
            if (employees.length > 0) {
                setLastUpdate(Date.now());
            }
        }, 60000); // Refresh every minute

        return () => clearInterval(interval);
    }, [employees, selectedView, lastUpdate]);

    if (loading && employees.length > 0) {
        return <div className="hours-summary loading">Loading hours data...</div>;
    }

    if (error) {
        return <div className="hours-summary error">Error: {error}</div>;
    }

    if (employees.length === 0) {
        return <div className="hours-summary empty">No employees found</div>;
    }

    return (
        <div className={`hours-summary ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="hours-summary-header">
                <h2>Working Hours Summary</h2>
                <button 
                    className={`toggle-button ${isCollapsed ? 'collapsed' : ''}`}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? "Expand" : "Collapse"}
                >
                    ▼
                </button>
            </div>
            <div className="view-selector">
                <button 
                    className={selectedView === 'weekly' ? 'active' : ''} 
                    onClick={() => setSelectedView('weekly')}
                >
                    Weekly
                </button>
                <button 
                    className={selectedView === 'monthly' ? 'active' : ''} 
                    onClick={() => setSelectedView('monthly')}
                >
                    Monthly
                </button>
                <button 
                    className={selectedView === 'yearly' ? 'active' : ''} 
                    onClick={() => setSelectedView('yearly')}
                >
                    Yearly
                </button>
                <button 
                    className="refresh-button"
                    onClick={() => calculateHoursData()}
                    title="Refresh hours data"
                >
                    ↻
                </button>
            </div>

            <div className="period-navigator">
                <button onClick={navigatePrevious}>←</button>
                <span className="current-period">
                    {getCurrentPeriodLabel()}
                    {selectedView === 'weekly' && (
                        <span style={{fontSize: '0.8em', color: '#666', marginLeft: '8px'}}>
                            (Week: {currentDate.clone().startOf('week').format('MMM D')} - {currentDate.clone().endOf('week').format('MMM D')})
                        </span>
                    )}
                </span>
                <button onClick={navigateNext}>→</button>
                <button 
                    className="today-button"
                    onClick={() => setCurrentDate(moment())}
                >
                    Today
                </button>
            </div>

            <div className="hours-grid">
                {employees.map(employee => (
                    <div key={employee.id} className="employee-hours">
                        <div className="employee-info">
                            <div className="avatar">
                                {employee.email[0].toUpperCase()}
                            </div>
                            <div className="details">
                                <div className="name">{employee.email.split('@')[0]}</div>
                                <div className="role">{employee.role || 'Employee'}</div>
                            </div>
                        </div>
                        <div className="hours-info">
                            <div className="total-hours">
                                {hoursData[employee.id]?.total || 0} hours
                            </div>
                            {selectedView === 'weekly' && hoursData[employee.id]?.daily && (
                                <div className="daily-breakdown">
                                    {Array.from({ length: 7 }).map((_, index) => {
                                        // Get the date for this column
                                        const columnDate = currentDate.clone().startOf('week').add(index, 'days');
                                        const columnDateStr = columnDate.format('YYYY-MM-DD');
                                        
                                        // Find matching day data
                                        const dayData = hoursData[employee.id].daily.find(d => {
                                            const matches = d.work_date === columnDateStr;
                                            console.log('Daily breakdown comparison:', {
                                                employeeId: employee.id,
                                                workDate: d.work_date,
                                                columnDate: columnDateStr,
                                                matches,
                                                hours: d.hours
                                            });
                                            return matches;
                                        });
                                        
                                        console.log('Rendering day column:', {
                                            columnDate: columnDateStr,
                                            dayOfWeek: columnDate.format('ddd'),
                                            employeeId: employee.id,
                                            hasData: !!dayData,
                                            hours: dayData?.hours
                                        });
                                        
                                        return (
                                            <div key={columnDateStr} className="day-hours">
                                                <span>{columnDate.format('ddd')}</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span>{dayData ? dayData.hours : 0}h</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HoursSummary; 